/**
 * Mobile menu behaviour.
 *
 * WHY THIS EXISTS AT ALL. `<details>` gives correct semantics and works with
 * JS off, but it has no in-between state: the panel appears and disappears
 * instantly while every other surface on this site eases. It also has no idea
 * about Escape, about clicks landing outside it, or about being navigated
 * away from.
 *
 * WHY IT IS DELEGATED FROM `document`. header-scroll.js presents the sticky
 * header as a CLONE of the source header, created after this file runs and
 * recreated on every `shopify:section:load`. Binding to the elements directly
 * would leave the clone's menu dead, so every listener here reads the DOM at
 * event time instead.
 *
 * WHY SCROLL IS LOCKED. There are two headers in the document and therefore
 * two menus. Scrolling with one open would hand the page to the other, whose
 * own menu is closed, and the panel would vanish mid-interaction while the
 * first stayed open behind it. Locking the root removes the desync at its
 * source rather than trying to mirror state between the two.
 */
const OPEN_CLASS = 'is-open';
const LOCK_CLASS = 'mobile-menu-open';
const CLOSE_FALLBACK_MS = 600;

/** @returns {boolean} */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** @returns {HTMLDetailsElement[]} */
function menus() {
  return /** @type {HTMLDetailsElement[]} */ ([...document.querySelectorAll('.site-header__mobile-menu')]);
}

function anyOpen() {
  return menus().some((menu) => menu.open);
}

function syncScrollLock() {
  document.documentElement.classList.toggle(LOCK_CLASS, anyOpen());
}

/** @param {HTMLDetailsElement} menu */
function openMenu(menu) {
  if (menu.open) return;
  menu.open = true;
  syncScrollLock();
  // One frame between `open` and the class, or the browser has no closed state
  // to transition FROM and the panel simply appears.
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => menu.classList.add(OPEN_CLASS));
  });
}

/**
 * @param {HTMLDetailsElement} menu
 * @param {boolean} [immediate] Skip the transition. Used when a navigation is
 *   already underway, so the closing animation cannot outlive the page.
 */
function closeMenu(menu, immediate) {
  if (!menu.open) return;
  menu.classList.remove(OPEN_CLASS);

  if (immediate || prefersReducedMotion()) {
    menu.open = false;
    syncScrollLock();
    return;
  }

  const panel = menu.querySelector('.site-header__mobile-panel');
  let settled = false;

  const finish = () => {
    if (settled) return;
    settled = true;
    panel?.removeEventListener('transitionend', onEnd);
    // Re-check: the menu may have been reopened during the close.
    if (!menu.classList.contains(OPEN_CLASS)) menu.open = false;
    syncScrollLock();
  };

  // Typed as a plain EventListener and narrowed inside: addEventListener's
  // signature takes Event, and a TransitionEvent-typed handler does not
  // satisfy it under checkJs.
  const onEnd = /** @type {EventListener} */ ((event) => {
    const transition = /** @type {TransitionEvent} */ (event);
    if (transition.target === panel && transition.propertyName === 'transform') finish();
  });

  panel?.addEventListener('transitionend', onEnd);
  // A panel that never fires transitionend (display swapped out from under it,
  // transitions disabled by an extension) must not leave the page scroll-locked.
  window.setTimeout(finish, CLOSE_FALLBACK_MS);
}

/** @param {boolean} [immediate] */
function closeAll(immediate) {
  menus().forEach((menu) => closeMenu(menu, immediate));
}

document.addEventListener('click', (event) => {
  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  if (!target) return;

  const toggle = target.closest('.site-header__menu-toggle');
  if (toggle) {
    // The default action would flip `open` instantly, which is the behaviour
    // being replaced.
    event.preventDefault();
    const menu = /** @type {HTMLDetailsElement | null} */ (toggle.closest('.site-header__mobile-menu'));
    if (!menu) return;
    if (menu.open) closeMenu(menu);
    else openMenu(menu);
    return;
  }

  if (!anyOpen()) return;

  // A link inside the panel is navigating. Close without animating so the
  // back/forward cache cannot restore the page with the panel still open.
  if (target.closest('.site-header__mobile-panel a')) {
    closeAll(true);
    return;
  }

  // Anything else outside the panel dismisses, the scrim included.
  if (!target.closest('.site-header__mobile-panel')) closeAll();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !anyOpen()) return;
  const open = menus().find((menu) => menu.open);
  closeAll();
  // Focus goes back to the control that opened it, not to the top of the page.
  /** @type {HTMLElement | null} */ (open?.querySelector('.site-header__menu-toggle'))?.focus();
});

// Above the breakpoint the panel is not rendered, so an open menu would leave
// the page scroll-locked with nothing visible to close.
window.matchMedia('(min-width: 52.01rem)').addEventListener('change', (event) => {
  if (event.matches) closeAll(true);
});

// A fresh clone from header-scroll.js can arrive carrying a stale `open`.
document.addEventListener('shopify:section:load', () => closeAll(true));

window.addEventListener('pageshow', () => closeAll(true));
